import { inject, Injectable, Signal } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { toSignal } from "@angular/core/rxjs-interop";
import { defer, EMPTY, fromEvent, merge, timer } from "rxjs";
import { filter, map, repeat } from "rxjs/operators";

/**
 * Shared "which local day is it" signal for the budget page. Interval-derived computeds (current-
 * interval spend, days left, reset date, …) call Date.now() inside `computed()`, but wall-clock time
 * is not a signal dependency, so they would otherwise cache the values from the moment they were
 * first read and go stale when a page left open crosses an interval reset (midnight on the 1st for
 * monthly, Sunday→Monday for weekly).
 *
 * Those computeds read `day()` only to register the dependency, and still measure with a fresh
 * Date.now() — so a transaction that lands between two ticks is never excluded by a stale "now".
 * `day()` holds the start of the current local day (unix ms), which only changes when the day does:
 * hourly ticks and refocuses within the same day are deduplicated by the signal, so they do not
 * recompute anything or give downstream objects (the header summary) a new identity.
 *
 * The day is re-read at the top of every local hour (a reset is always on an hour boundary, so the
 * page is current within moments of midnight) and whenever the page comes back to the foreground —
 * tab visible again, window focused, or restored from the back/forward cache — which covers a
 * device waking from sleep (timers are throttled/suspended while hidden).
 */
@Injectable({ providedIn: "root" })
export class BudgetClockService {
  private readonly _document = inject(DOCUMENT);
  private readonly _window = this._document.defaultView;

  /** Start of the current local day (unix ms); changes only when the local day does. */
  readonly day: Signal<number> = toSignal(
    merge(
      // defer + repeat re-measures the wait after every tick, so the ticks stay on the hour
      // instead of drifting like a fixed 60-minute interval would.
      defer(() => timer(BudgetClockService._msUntilNextHour())).pipe(repeat()),
      fromEvent(this._document, "visibilitychange").pipe(filter(() => !this._document.hidden)),
      this._window ? fromEvent(this._window, "focus") : EMPTY,
      this._window ? fromEvent(this._window, "pageshow") : EMPTY,
    ).pipe(map(() => BudgetClockService._startOfToday())),
    { initialValue: BudgetClockService._startOfToday() },
  );

  private static _startOfToday(): number {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  }

  private static _msUntilNextHour(): number {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0);
    return nextHour.getTime() - now.getTime();
  }
}
